import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/pages/AppShell";
import { v1 } from "@/lib/api";
import { useOrg } from "@/lib/orgContext";
import { Building, Plus, ArrowRight } from "iconoir-react";

export default function Organizations() {
  const navigate = useNavigate();
  const { setActiveOrgId, refresh } = useOrg();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await v1.orgs.list();
      setOrgs(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const org = await v1.orgs.create({ name: name.trim() });
      toast.success("Organization created");
      setName("");
      setActiveOrgId(org.id);
      await refresh();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create organization");
    } finally {
      setCreating(false);
    }
  };

  const switchTo = async (id) => {
    setActiveOrgId(id);
    await refresh();
    toast.success("Active organization switched");
  };

  return (
    <AppShell title="Organizations" subtitle="WORKSPACE">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="organizations-list">
          {loading && <div className="font-mono text-xs text-muted-foreground">Loading…</div>}
          {!loading && orgs.length === 0 && (
            <div className="border border-border p-6 text-sm text-muted-foreground">
              You have no organizations yet. Create one on the right.
            </div>
          )}
          {orgs.map((o) => (
            <div
              key={o.id}
              data-testid={`org-card-${o.slug}`}
              className="border border-border p-5 flex items-center justify-between hover:bg-card/40 transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 border border-border flex items-center justify-center">
                  <Building width={18} height={18} strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <div className="font-app font-medium truncate">{o.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {o.slug} · plan: {o.plan_key} {o.is_personal && "· personal"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => switchTo(o.id)}
                  data-testid={`org-switch-${o.slug}`}
                  className="px-3 py-1.5 border border-border text-xs font-app uppercase tracking-widest hover:bg-card"
                >
                  Switch
                </button>
                <button
                  onClick={() => navigate(`/organizations/${o.id}/members`)}
                  data-testid={`org-members-${o.slug}`}
                  className="px-3 py-1.5 bg-obsidian text-bone text-xs font-app uppercase tracking-widest flex items-center gap-2 hover:opacity-90"
                >
                  Members <ArrowRight width={12} height={12} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <form
            onSubmit={handleCreate}
            data-testid="org-create-form"
            className="border border-border p-5 space-y-3"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Create organization</div>
            <input
              type="text"
              placeholder="Workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={80}
              data-testid="org-create-name"
              className="w-full border border-border px-3 py-2 bg-background text-sm font-app"
            />
            <button
              type="submit"
              disabled={creating}
              data-testid="org-create-submit"
              className="w-full flex items-center justify-center gap-2 py-2 bg-obsidian text-bone text-xs font-app uppercase tracking-widest disabled:opacity-60"
            >
              <Plus width={14} height={14} /> {creating ? "Creating…" : "Create organization"}
            </button>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
